import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/lib/supabase';
import type { UserWithCourses } from '@/lib/types';
import { uniqueByCourseId } from '@/lib/utils';

const schoolId = import.meta.env.VITE_SCHOOL_ID;

export function useGetUsers() {
	return useQuery<UserWithCourses[]>({
		queryKey: ['get-users', schoolId],

		queryFn: async () => {
			const { data, error } = await supabase
				.from('users')
				.select(
					`
					user_id,
					user_email,
					user_firebase_id,
					user_first,
					user_last,
					user_role,
					user_dept_chair,
					courses_users (
						courses_users_id,
						user_id,
						course_id,
						course:courses (
							course_name,
							course_id
						)
					)
				`
				)
				.eq('school_id', schoolId)
				.order('user_first', { ascending: true });

			if (error) throw error;

			// Fetch courses relationship (direct courses_users relationship)
			// This is a separate query to get courses for each user
			const userIds = data?.map((user) => user.user_id) ?? [];
			let coursesMap: Record<string, Array<{ course_id: string; course_name: string }>> = {};

			if (userIds.length > 0) {
				const { data: coursesUsers } = await supabase
					.from('courses_users')
					.select(
						`
						user_id,
						course:courses!courses_users_course_id_fkey (
							course_id,
							course_name
						)
					`
					)
					.in('user_id', userIds);

				coursesMap =
					coursesUsers?.reduce(
						(acc, cu) => {
							if (!acc[cu.user_id]) {
								acc[cu.user_id] = [];
							}
							const assigned = cu.course;
							if (
								assigned &&
								!acc[cu.user_id].some((course) => course.course_id === assigned.course_id)
							) {
								acc[cu.user_id].push({
									course_id: assigned.course_id,
									course_name: assigned.course_name,
								});
							}
							return acc;
						},
						{} as Record<string, Array<{ course_id: string; course_name: string }>>
					) ?? {};
			}

			// Fetch dept_chair information for users that have a dept_chair
			// Note: user_dept_chair stores Firebase UIDs
			const deptChairIds =
				data
					?.map((user: any) => user.user_dept_chair)
					.filter((id: string | null | undefined): id is string => !!id) ?? [];

			let deptChairMap: Record<
				string,
				{ user_id: string; user_first: string | null; user_last: string | null }
			> = {};
			if (deptChairIds.length > 0) {
				const { data: deptChairs } = await supabase
					.from('users')
					.select('user_firebase_id, user_id, user_first, user_last')
					.in('user_firebase_id', deptChairIds);

				deptChairMap =
					deptChairs?.reduce(
						(acc, dc) => {
							acc[dc.user_firebase_id] = {
								user_id: dc.user_id,
								user_first: dc.user_first,
								user_last: dc.user_last,
							};
							return acc;
						},
						{} as Record<
							string,
							{ user_id: string; user_first: string | null; user_last: string | null }
						>
					) ?? {};
			}

			// Transform the data to match the expected shape
			const transformedData = data?.map((user: any) => ({
				...user,
				// Transform user_dept_chair string to dept_chair object
				dept_chair: user.user_dept_chair ? (deptChairMap[user.user_dept_chair] ?? null) : null,
				courses_users: uniqueByCourseId(user.courses_users ?? []),
				// Add courses relationship
				courses: coursesMap[user.user_id] ?? [],
			}));

			return (transformedData ?? []) as unknown as UserWithCourses[];
		},

		staleTime: Infinity,
		refetchOnMount: 'always',
	});
}
